import React, { useState, useEffect, useRef } from "react";
import { ChevronUp, ArrowLeft, Plus, Eye, Upload, ImageOff } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Import your icon assets here
import Icon1 from "@/assets/home/card/Icon1.svg";
import Icon2 from "@/assets/home/card/Icon2.svg";
import Icon3 from "@/assets/home/card/Icon3.svg";
import Icon4 from "@/assets/home/card/Icon4.svg";
import Icon5 from "@/assets/home/card/Icon5.svg";
import Icon6 from "@/assets/home/card/Icon6.svg";
import Dropdown, { Options } from "@/components/ui/DropDown";

import CompanyLogo from "@/assets/Dashboard_Card.png";
import DashboardCard from "@/assets/Dashboard_Card.png";
import useCampaignDetailStore from "../store/useCampaignDetailStore";
import type { DisplayPayload } from "../store/useCampaignDetailStore";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";
import { isAlreadyRestored } from "@/api/restore";
import "../css/index.css";
import Button from "@/components/ui/Button";
import DeleteConfirmationControl from "./delete-confirmation-control";
import { useResolvedPreview } from "./use-resolved-preview";

// Type definitions
interface AssetsCardProps {
  imgUrl: string;
  progress1: number;
  progress2: number;
  progress3: number;
  progress4: number;
  progress5: number;
  progress6: number;
  campaignId: string;
  adTypeId: string;
  assetsRowId?: string | null;
  platformName?: string;
  count?: number;
  aspectRatio?: string; // New prop for dynamic aspect ratio (e.g., "16/9", "4/3", "1/1")
  displayPayload?: DisplayPayload; // Per-Box preview data from the asset-preview-pipeline
  onDelete?: (assetsRowId: string) => Promise<void>;
  onDeleteError?: (message: string) => void;
  // Per-Box manual override handlers (asset-preview-pipeline, Requirement 8).
  // Scoped to this Box's asset row (assetsRowId). Resolve after the dashboard
  // has been refreshed so the card reflects the new source.
  onUploadOverride?: (assetsRowId: string, file: File) => Promise<void>;
  onRemoveOverride?: (assetsRowId: string) => Promise<void>;
}

interface PlatformCard {
  /** The Box's asset_id — never `ad_type_id`, which siblings share. */
  id: string;
  adTypeId: string;
  assetsRowId?: string | null;
  /** Display label; carries the region when siblings share an ad type. */
  name: string;
  /** The bare ad type name, for filters and searches that match on it. */
  adTypeName?: string;
  /** Publisher owning this ad type, shown above the tile to disambiguate
   *  type names that several platforms share ("Story", "Carousel"). */
  publisherName?: string | null;
  region?: string | null;
  count: number;
  imgUrl: string;
  progress: {
    progress1: number;
    progress2: number;
    progress3: number;
    progress4: number;
    progress5: number;
    progress6: number;
  };
  campaignId: string;
  isPlaceholder?: boolean;
  aspectRatio?: string; // New field for aspect ratio
  displayPayload?: DisplayPayload; // Per-Box preview data from the asset-preview-pipeline
}

interface Section {
  title: string;
  count: number;
  colorBar: string[];
  platforms: PlatformCard[];
}

// Per-Box manual override upload constraints — must mirror the backend
// validation for POST .../asset/{assetId}/preview-override (jpg/jpeg/png, ≤8 MB).
const OVERRIDE_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
] as const;
const OVERRIDE_MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024; // 8 MB

// Validate a candidate override file client-side before uploading. Returns an
// error message when the file is not an allowed type or is too large, otherwise
// null. Keeps the failure UX consistent with the server's 422 messages.
const validateOverrideFile = (file: File): string | null => {
  const isAllowedType = OVERRIDE_ALLOWED_MIME_TYPES.includes(
    file.type as (typeof OVERRIDE_ALLOWED_MIME_TYPES)[number],
  );
  if (!isAllowedType) {
    return "Allowed image types are: jpg, jpeg, png.";
  }
  if (file.size > OVERRIDE_MAX_FILE_SIZE_BYTES) {
    return "Image must be 8 MB or smaller.";
  }
  return null;
};

// AssetsCard Component with Dynamic Aspect Ratio
const AssetsCard: React.FC<AssetsCardProps> = ({
  imgUrl,
  progress1,
  progress2,
  progress3,
  progress4,
  progress5,
  progress6,
  campaignId,
  adTypeId,
  assetsRowId,
  platformName,
  count,
  aspectRatio = "auto", // Default to "auto" if not specified
  displayPayload,
  onDelete,
  onDeleteError,
  onUploadOverride,
  onRemoveOverride,
}) => {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [calculatedRatio, setCalculatedRatio] = useState<string>("auto");
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Per-Box manual override UI state. The file input is unique to this card so
  // two Boxes sharing an ad_type stay independent (Requirement 9).
  const overrideInputRef = useRef<HTMLInputElement | null>(null);
  const [isOverrideBusy, setIsOverrideBusy] = useState(false);
  const hasManualOverride = displayPayload?.source === "manual";

  const handleOverrideFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Reset the input value up front so re-selecting the same file re-triggers.
    if (overrideInputRef.current) overrideInputRef.current.value = "";
    if (!file || !assetsRowId || !onUploadOverride) return;

    // Validate type/size before hitting the network; surface via the same
    // error channel used for server errors.
    const validationError = validateOverrideFile(file);
    if (validationError) {
      onDeleteError?.(validationError);
      return;
    }

    try {
      setIsOverrideBusy(true);
      await onUploadOverride(assetsRowId, file);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to upload preview override.";
      onDeleteError?.(message);
    } finally {
      setIsOverrideBusy(false);
    }
  };

  const handleRemoveOverride = async () => {
    if (!assetsRowId || !onRemoveOverride) return;
    try {
      setIsOverrideBusy(true);
      await onRemoveOverride(assetsRowId);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to remove preview override.";
      onDeleteError?.(message);
    } finally {
      setIsOverrideBusy(false);
    }
  };

  // Resolve the preview to render from the Box's display_payload (manual/auto/none,
  // including cycling). Falls back to the placeholder image (imgUrl) when there
  // is nothing displayable.
  const { imageUrl: resolvedImageUrl } = useResolvedPreview(displayPayload);
  const effectiveImageUrl = resolvedImageUrl ?? imgUrl;

  // Calculate aspect ratio from image if aspectRatio is "auto"
  useEffect(() => {
    if (aspectRatio === "auto" && effectiveImageUrl) {
      const img = new Image();
      img.src = effectiveImageUrl;
      img.onload = () => {
        const ratio = img.width / img.height;
        // Convert to common ratio format
        let ratioString = "1 / 1"; // Default square

        if (ratio > 1.7) {
          ratioString = "16 / 9"; // Wide
        } else if (ratio > 1.4) {
          ratioString = "3 / 2"; // Medium wide
        } else if (ratio > 1.2) {
          ratioString = "4 / 3"; // Slightly wide
        } else if (ratio > 0.9 && ratio < 1.1) {
          ratioString = "1 / 1"; // Square
        } else if (ratio < 0.8) {
          ratioString = "9 / 16"; // Portrait
        }

        setCalculatedRatio(ratioString);
      };
    } else if (aspectRatio !== "auto") {
      setCalculatedRatio(aspectRatio);
    }
  }, [effectiveImageUrl, aspectRatio]);

  const icons = [
    [
      { src: Icon2, alt: "icon 2", number: progress1 },
      { src: Icon3, alt: "icon 3", number: progress2 },
      { src: Icon4, alt: "icon 4", number: progress3 },
    ],
    [
      { src: Icon1, alt: "icon 1", number: progress4 },
      { src: Icon6, alt: "icon 6", number: progress5 },
      { src: Icon5, alt: "icon 5", number: progress6 },
    ],
  ];

  return (
    <div
      className="w-full rounded-2xl relative hover-card overflow-hidden"
      style={{
        aspectRatio: calculatedRatio,
        minHeight: "330px",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Per-Box delete control, scoped to this card's own asset row. `assetsRowId` is
          the Box's asset_id, so a flight row carrying several Boxes of the same ad type
          deletes the one that was clicked. */}
      {assetsRowId && onDelete && (
        <div className="absolute top-2 right-2 z-20">
          <DeleteConfirmationControl
            itemLabel={`${platformName ?? "this"} asset`}
            ariaLabel="Delete asset"
            title="Delete asset?"
            description="This removes the asset, its versions, and its stored media from the campaign. The records and media are retained for 30 days, and an export of everything deleted is emailed to you."
            onConfirm={() => onDelete(assetsRowId)}
            onError={onDeleteError}
            triggerClassName="bg-white/90 hover:bg-white text-gray-600 shadow"
          />
        </div>
      )}

      {/* Preview: the resolved/cycling image when one exists, otherwise the
          placeholder. No loading state — see useResolvedPreview. */}
      <div
        className="bg-cover bg-no-repeat bg-center w-full h-full absolute inset-0 transition-transform duration-500 ease-out"
        style={{
          backgroundImage: `url(${effectiveImageUrl})`,
          transform: hover ? "scale(1.1)" : "scale(1)",
        }}
      />

      {/* Overlay */}
      <div
        className={`w-full h-full p-0 transition-opacity duration-300 absolute inset-0 rounded-2xl hover-overlay ${
          hover ? "opacity-100 bg-black bg-opacity-70" : "opacity-0"
        }`}
      >
        <div className="p-0 flex w-full h-full flex-col gap-7 justify-center items-center card-drop rounded-2xl">
          <div className="flex w-full justify-center z-10 relative">
            {icons.map((iconGroup, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-3.5 items-center justify-center"
              >
                {iconGroup.map(({ src, alt, number }, i) => (
                  <div key={i} className="flex mr-6 ml-4 my-1">
                    <img
                      src={src}
                      alt={alt}
                      className="w-8 h-8 filter drop-shadow-lg"
                    />
                    <p className="w-8 h-8 flex justify-center items-center text-white">
                      {number}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <Button
            className="z-10 relative"
            type="outline"
            trailingIcon={<Eye />}
            onClick={() => {
              const params = new URLSearchParams({ adTypeId });
              if (assetsRowId) params.set("assetsRowId", assetsRowId);
              navigate(
                `/campaign/${campaignId}/ad-builder?${params.toString()}`,
              );
            }}
          >
            View Assets
          </Button>

          {/* Per-Box manual override controls (asset-preview-pipeline, Req 8).
              Only available once the Box has an asset row to key the override on. */}
          {assetsRowId && onUploadOverride && (
            <div className="z-10 relative flex items-center gap-2">
              <input
                ref={overrideInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleOverrideFileChange}
                className="hidden"
              />
              <Button
                type="outline"
                disabled={isOverrideBusy}
                leadingIcon={<Upload size={16} />}
                onClick={() => overrideInputRef.current?.click()}
              >
                {isOverrideBusy
                  ? "Working..."
                  : hasManualOverride
                    ? "Replace preview"
                    : "Upload preview"}
              </Button>
              {hasManualOverride && onRemoveOverride && (
                <Button
                  type="outline"
                  disabled={isOverrideBusy}
                  leadingIcon={<ImageOff size={16} />}
                  onClick={handleRemoveOverride}
                >
                  Remove
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CampaignDetailAdCategoryDetails: React.FC = () => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams] = useSearchParams();

  const languageId = searchParams.get("languageId");
  const objectiveId = searchParams.get("objectiveId");

  const { showSuccess, showError, showInfo } = useGlobalStore();

  const {
    languageObjectiveDashboard,
    getLanguageObjectiveDashboard,
    getCampaignDetailData,
    deleteAsset,
    restoreAsset,
    putS3Image,
    uploadAssetsDashboardImage,
    uploadAssetPreviewOverride,
    removeAssetPreviewOverride,
  } = useCampaignDetailStore();

  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Updated to use string values for the custom Dropdown component
  const [filters, setFilters] = useState({
    status: "",
    region: "",
    publication: "",
  });

  const fetchData = async () => {
    if (!campaignId || !languageId || !objectiveId) {
      showError(
        "Missing required parameters: campaignId, languageId, or objectiveId",
      );
      return;
    }

    try {
      setIsLoading(true);
      await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);
    } catch (error) {
      showError(error.message ?? "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  // Re-fetch everything this page draws from, shared by the delete and its undo.
  const refreshDashboards = async () => {
    if (campaignId) {
      await getCampaignDetailData(campaignId);
    }
    if (campaignId && languageId && objectiveId) {
      await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);
    }
  };

  // Undo of a just-completed delete, run straight from the toast. A 404
  // NO_RESTORABLE_DELETION means the asset is already back, which is not a
  // failure; a 409 carries the ancestor message, which is shown verbatim.
  const handleUndoDeleteAsset = async (assetsRowId: string): Promise<void> => {
    try {
      await restoreAsset(assetsRowId);
      await refreshDashboards();
      showSuccess("Asset restored.");
    } catch (error: any) {
      if (isAlreadyRestored(error)) {
        await refreshDashboards();
        showInfo("That asset has already been restored.");
        return;
      }
      showError(error?.message ?? "Failed to restore asset");
    }
  };

  // Delete an asset (by its assets_row_id) and refresh the displayed data.
  // Errors (including 423 lock messages) are surfaced by the control.
  const handleDeleteAsset = async (assetsRowId: string) => {
    await deleteAsset(assetsRowId);
    await refreshDashboards();
    showSuccess("Asset deleted.", undefined, {
      label: "Undo",
      onClick: () => handleUndoDeleteAsset(assetsRowId),
    });
  };

  // Upload/replace a per-Box manual preview override, then refresh the dashboard
  // so the Box's display_payload flips to source=manual. Scoped by assetsRowId
  // (asset_id) so Boxes sharing an ad_type stay independent.
  const handleUploadOverride = async (assetsRowId: string, file: File) => {
    if (!campaignId) return;
    await uploadAssetPreviewOverride(campaignId, assetsRowId, file);
    if (campaignId && languageId && objectiveId) {
      await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);
    }
    showSuccess("Preview override uploaded.");
  };

  // Remove a Box's manual preview override, then refresh so it reverts to auto.
  const handleRemoveOverride = async (assetsRowId: string) => {
    if (!campaignId) return;
    await removeAssetPreviewOverride(campaignId, assetsRowId);
    if (campaignId && languageId && objectiveId) {
      await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);
    }
    showSuccess("Preview override removed.");
  };

  // Handle thumbnail upload
  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  // Handle thumbnail upload (updated to support initiate → PUT → confirm flow)
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file || !campaignId || !languageId || !objectiveId) return;

    try {
      setIsUploading(true);

      // 🧩 Step 1 — Initiate upload (ask backend for presigned URL)
      const initiateBody = {
        campaign_id: campaignId,
        type: "assets_dashboard",
        objective_id: objectiveId,
        language_id: languageId,
        filename: file.name, // ✅ send filename instead of key
        mime: file.type,
      };

      const initiateResponse = await uploadAssetsDashboardImage(
        campaignId,
        initiateBody,
      );

      // 🧩 Step 2 — Upload to S3 using presigned URL
      const uploadUrl =
        "upload_url" in initiateResponse.data
          ? initiateResponse.data.upload_url
          : null;

      if (!uploadUrl) {
        throw new Error("Upload URL missing in initiation response");
      }

      await putS3Image(uploadUrl, file);

      // 🧩 Step 3 — Confirm upload to backend
      const confirmBody = {
        campaign_id: campaignId,
        type: "assets_dashboard",
        objective_id: objectiveId,
        language_id: languageId,
        key: initiateResponse.data.key, // ✅ backend expects the key after upload
        mime: file.type,
      };

      await uploadAssetsDashboardImage(campaignId, confirmBody);

      // Optionally refresh the dashboard data to show new thumbnail
      // await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);

      showSuccess("Image uploaded and confirmed successfully!");
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to upload thumbnail. Please try again.";
      showError(errorMessage);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // const handleFileChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];
  //   if (!file || !campaignId || !languageId || !objectiveId) return;

  //   try {
  //     setIsUploading(true);

  //     // Generate the key for the image
  //     const key = `${campaignId}/${file.name}`;

  //     // Prepare the request body
  //     const requestBody = {
  //       campaign_id: campaignId,
  //       type: "assets_dashboard",
  //       objective_id: objectiveId,
  //       language_id: languageId,
  //       key: key,
  //       mime: file.type,
  //     };

  //     // First, call the backend to confirm the header image
  //     const response = await uploadAssetsDashboardImage(
  //       campaignId,
  //       requestBody
  //     );

  //     // If the response contains a presigned URL, upload the file to S3
  //     if (response.data.url) {
  //       await putS3Image(response.data.url, file);
  //     }

  //     // Optionally refresh the dashboard data to show the new image
  //     // await getLanguageObjectiveDashboard(campaignId, languageId, objectiveId);

  //     showSuccess("Image uploaded successfully!");
  //   } catch (error) {
  //     console.error("Error uploading thumbnail:", error);
  //     const errorMessage =
  //       error instanceof Error
  //         ? error.message
  //         : "Failed to upload thumbnail. Please try again.";
  //     showError(errorMessage);
  //   } finally {
  //     setIsUploading(false);
  //     // Reset file input
  //     if (fileInputRef.current) {
  //       fileInputRef.current.value = "";
  //     }
  //   }
  // };

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchData();
  }, [campaignId, languageId, objectiveId]);

  // Auto-expand all sections on load
  useEffect(() => {
    if (languageObjectiveDashboard?.nested_by_ad_category) {
      const expanded: Record<string, boolean> = {};
      languageObjectiveDashboard.nested_by_ad_category.forEach((_, index) => {
        expanded[`section-${index}`] = true;
      });
      setExpandedSections(expanded);
    }
  }, [languageObjectiveDashboard]);

  // Get unique regions from nested_by_flight data
  const availableRegions = Array.from(
    new Set(
      languageObjectiveDashboard?.nested_by_flight?.flatMap((flight) =>
        flight.flight_rows?.map((row) => row?.region),
      ) || [],
    ),
  );

  // Get unique ad category names (publications) from nested_by_ad_category
  const availablePublications = Array.from(
    new Set(
      languageObjectiveDashboard?.nested_by_ad_category?.map(
        (category) => category?.ad_category_name,
      ) || [],
    ),
  );

  // Filter options for the dropdowns
  const filterOptions: Record<string, Options[]> = {
    status: [
      { title: "Active", value: "active" },
      { title: "Draft", value: "draft" },
      { title: "Completed", value: "completed" },
    ],
    region: availableRegions.map((region) => ({
      title: region,
      value: region?.toLowerCase().replace(/\s+/g, "-"),
    })),
    publication: availablePublications.map((publication) => ({
      title: publication,
      value: publication.toLowerCase().replace(/\s+/g, "-"),
    })),
  };

  // Generate color bar from ad types
  const generateColorBar = (adTypes: any[]) => {
    const colors = [
      "#fde047",
      "#f97316",
      "#ef4444",
      "#a855f7",
      "#3b82f6",
      "#06b6d4",
      "#fb923c",
      "#10b981",
    ];
    return adTypes.map((_, index) => colors[index % colors.length]);
  };

  // Per-Box preview data, keyed by asset_id.
  //
  // This used to be a lookup from ad_type_name → asset_id, built by walking the flight
  // nesting. That key collides: a flight row can carry several Boxes of the same ad type
  // (one per region), so the last one written won and every card of that ad type resolved
  // the same asset — one shared preview, one shared override target, and one shared
  // ad-builder link for what are independent assets. The backend now emits `asset_id` and
  // `display_payload` on each ad-category entry, so no cross-payload join is needed; this
  // map only remains as a fallback for a cached response predating that change.
  const displayPayloadByAssetId: Record<string, DisplayPayload> = {};
  languageObjectiveDashboard?.nested_by_flight?.forEach((flight) => {
    flight.flight_rows?.forEach((row) => {
      row.flight_ad_categories?.forEach((cat) => {
        cat.ad_rows?.forEach((adRow) => {
          if (adRow.asset_id && adRow.display_payload) {
            displayPayloadByAssetId[adRow.asset_id] = adRow.display_payload;
          }
        });
      });
    });
  });

  // Convert API data to sections
  const allSections: Section[] =
    languageObjectiveDashboard?.nested_by_ad_category.map((category) => ({
      title: category.ad_category_name,
      count: category.status_counts?.total ?? category.total_ads,
      colorBar: generateColorBar(category.ad_types),
      platforms: category.ad_types.map((adType) => {
        const assetsRowId = adType.asset_id ?? null;

        // Siblings sharing an ad type are told apart by their flight row's region, so the
        // card names the region rather than repeating the ad type name verbatim.
        const hasSibling =
          category.ad_types.filter(
            (other) => other.ad_type_id === adType.ad_type_id,
          ).length > 1;
        const label =
          hasSibling && adType.region
            ? `${adType.ad_type_name} — ${adType.region}`
            : adType.ad_type_name;

        return {
          // The Box's own identity: `ad_type_id` is shared by siblings, so using it as a
          // React key made the list reuse the wrong card between them.
          id: assetsRowId ?? adType.ad_type_id,
          adTypeId: adType.ad_type_id,
          assetsRowId,
          displayPayload:
            adType.display_payload ??
            (assetsRowId ? displayPayloadByAssetId[assetsRowId] : undefined),
          name: label,
          adTypeName: adType.ad_type_name,
          publisherName: adType.publisher_name ?? null,
          region: adType.region ?? null,
          count: adType.status_counts?.total ?? 0,
          imgUrl: DashboardCard, // Default card image when no asset image
          progress: {
            progress1: adType.status_counts?.briefed ?? 0,
            progress2: adType.status_counts?.in_progress ?? 0,
            progress3: adType.status_counts?.in_review ?? 0,
            progress4: adType.status_counts?.approved ?? 0,
            progress5: adType.status_counts?.trafficking ?? 0,
            progress6: adType.status_counts?.live ?? 0,
          },
          campaignId: campaignId || "",
          aspectRatio: adType.ratio || "1 / 1",
        };
      }),
    })) || [];

  // Filter sections based on selected filters
  const sections = allSections.filter((section) => {
    // Filter by publication (ad category name pattern matching)
    if (filters.publication) {
      const publicationMatch = section.title
        .toLowerCase()
        .includes(filters.publication.toLowerCase());
      if (!publicationMatch) return false;
    }

    // Filter by status - using campaign status from metadata
    if (filters.status) {
      const campaignStatus =
        languageObjectiveDashboard?.metadata.campaign.status;
      if (campaignStatus !== filters.status) return false;
    }

    // Filter by region — keep the section when any of its Boxes sits in that region.
    //
    // Each Box now carries its own region, so this is a direct comparison. It used to walk
    // the flight nesting and match `ad.ad_type === platform.name`, which broke as soon as
    // a card's label carried anything but the bare ad type name, and could not tell two
    // same-ad-type Boxes in different regions apart anyway.
    if (filters.region) {
      const slug = (value: string) => value.toLowerCase().replace(/\s+/g, "-");

      const hasRegionMatch = section.platforms.some((platform) =>
        platform.region ? slug(platform.region) === filters.region : false,
      );

      if (!hasRegionMatch) return false;
    }

    return true;
  });

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const PlaceholderCard: React.FC = () => (
    <div className="bg-white rounded-2xl p-8 flex items-center justify-center h-[330px] border-2 border-gray-100">
      <button className="flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 transition-colors">
        <Plus size={48} strokeWidth={1} />
        <span className="mt-2 text-sm">Add content</span>
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (!languageObjectiveDashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">No dashboard data found</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-semibold">
            {languageObjectiveDashboard?.metadata.objective.name || "Objective"}
          </h1>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleThumbnailClick}
            disabled={isUploading}
            className="text-gray-500 hover:text-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-2 text-sm underline">
              <span className="mr-3">
                {isUploading ? "Uploading..." : "Edit Thumbnail"}
              </span>
              <img
                src={
                  languageObjectiveDashboard?.metadata.campaign
                    .campaign_poster || CompanyLogo
                }
                className="w-10 h-10 rounded"
              />
            </span>
          </button>
        </div>
      </div>

      {/* Filters - Updated to use custom Dropdown component */}
      <div className="flex items-center gap-4 mb-8 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Filter by:</span>

        {/* Status Filter */}
        <Dropdown
          selected={filters.status}
          setSelected={(value) =>
            setFilters((prev) => ({ ...prev, status: value.toString() }))
          }
          options={filterOptions.status}
          placeholder="Status"
          className="w-48"
        />

        {/* Region Filter */}
        <Dropdown
          selected={filters.region}
          setSelected={(value) =>
            setFilters((prev) => ({ ...prev, region: value.toString() }))
          }
          options={filterOptions.region}
          placeholder="Region"
          className="w-48"
        />

        {/* Publication Filter */}
        <Dropdown
          selected={filters.publication}
          setSelected={(value) =>
            setFilters((prev) => ({ ...prev, publication: value.toString() }))
          }
          options={filterOptions.publication}
          placeholder="Publication"
          className="w-48"
        />

        {/* Clear Filters Button */}
        {(filters.status || filters.region || filters.publication) && (
          <Button
            type="text"
            onClick={() =>
              setFilters({ status: "", region: "", publication: "" })
            }
            className="text-sm text-pink-600 hover:text-pink-700 underline font-medium"
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-2">No categories found</p>
            <p className="text-sm">
              Try adjusting your filters to see more results
            </p>
            {(filters.status || filters.region || filters.publication) && (
              <Button
                type="text"
                onClick={() =>
                  setFilters({ status: "", region: "", publication: "" })
                }
                className="mt-4 text-sm text-pink-600 hover:text-pink-700 underline font-medium"
              >
                Clear All Filters
              </Button>
            )}
          </div>
        ) : (
          sections.map((section, index) => {
            const sectionKey = `section-${index}`;
            const isExpanded = expandedSections[sectionKey];

            return (
              <div key={sectionKey} className="rounded-xl">
                {/* Section Header */}
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {section.title}
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-2 rounded-full overflow-hidden"
                        style={{ width: "120px" }}
                      >
                        {section.colorBar.map((color, index) => (
                          <div
                            key={index}
                            className="h-full"
                            style={{
                              backgroundColor: color,
                              width: `${100 / section.colorBar.length}%`,
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-lg font-semibold text-gray-700">
                        {section.count}
                      </span>
                    </div>
                    <button
                      onClick={() => toggleSection(sectionKey)}
                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      <ChevronUp
                        size={20}
                        className={`transition-transform duration-200 ${
                          isExpanded ? "rotate-0" : "rotate-180"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <hr className="mt-2 border-1 border-gray-300 mb-2" />

                {/* Platform Cards Grid - Masonry Layout */}
                {isExpanded && section.platforms.length > 0 && (
                  <div
                    className="masonry-grid"
                    style={{
                      columnCount: 4,
                      columnGap: "1rem",
                    }}
                  >
                    {section.platforms.map((platform) =>
                      platform.isPlaceholder ? (
                        <div key={platform.id} className="masonry-item">
                          <PlaceholderCard />
                        </div>
                      ) : (
                        <div key={platform.id} className="masonry-item">
                          {/* Publisher + ad type above the tile. Several
                              platforms reuse type names ("Story", "Carousel"),
                              so the publisher is what disambiguates them. */}
                          <div
                            className="text-sm font-medium text-gray-900 mb-1.5 truncate"
                            title={
                              platform.publisherName
                                ? `${platform.publisherName} · ${platform.adTypeName}`
                                : platform.adTypeName
                            }
                          >
                            {platform.publisherName
                              ? `${platform.publisherName} · ${platform.adTypeName}`
                              : platform.adTypeName}
                          </div>
                          <AssetsCard
                            imgUrl={platform.imgUrl}
                            progress1={platform.progress.progress1}
                            progress2={platform.progress.progress2}
                            progress3={platform.progress.progress3}
                            progress4={platform.progress.progress4}
                            progress5={platform.progress.progress5}
                            progress6={platform.progress.progress6}
                            campaignId={platform.campaignId}
                            adTypeId={platform.adTypeId}
                            assetsRowId={platform.assetsRowId}
                            platformName={platform.name}
                            count={platform.count}
                            aspectRatio={platform.aspectRatio}
                            displayPayload={platform.displayPayload}
                            onDelete={handleDeleteAsset}
                            onDeleteError={showError}
                            onUploadOverride={handleUploadOverride}
                            onRemoveOverride={handleRemoveOverride}
                          />
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CampaignDetailAdCategoryDetails;
